import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import {
  DashboardEconomyChangeItem,
  DashboardEconomyChangesMeasure,
  DashboardEconomyTimelineEntry,
} from '@interfaces/dashboard/responses/dashboard-economy-changes.response';
import { TabViewItem } from '@interfaces/a-organizar/tabViewItem';
import { TabViewComponent } from '@shared/components/tab-view/tab-view.component';
import { VaultDatePipe } from '@shared/pipes/vault-date.pipe';
import { VaultDateTimePipe } from '@shared/pipes/vault-date-time.pipe';
import { VaultFormatCurrencyPipe } from '@shared/pipes/vault-format-currency.pipe';
import {
  ECONOMY_CHANGE_CATEGORY_HINTS,
  ECONOMY_CHANGE_CATEGORY_LABELS,
  EconomyMeasure,
  economyTriggerLabel,
} from '@src/app/features/dashboard/dashboard-management/pages/economy-history/economy-history.labels';
import {
  buildEconomyChangeSummary,
  changedItems,
  isZeroAmount,
  unchangedCategories,
} from '@src/app/features/dashboard/dashboard-management/pages/economy-history/economy-history.summary';
import { EconomyOperationsChangesComponent } from '@src/app/features/dashboard/dashboard-management/pages/economy-history/components/economy-operations-changes/economy-operations-changes.component';

type BreakdownTab = 'reason' | 'operation';

/**
 * "O que mudou" do Histórico da economia (card 3295, redesenho no card 3323): uma frase que explica
 * a variação, a economia no início e no fim, e duas abas. "Por motivo" lista só os motivos que
 * mexeram (os zerados viram uma linha "Sem mudança"); "Por operação" mostra o antes e depois de cada
 * empréstimo. A soma dos motivos é a variação.
 */
@Component({
  selector: 'app-economy-changes-breakdown',
  standalone: true,
  imports: [
    CommonModule,
    VaultFormatCurrencyPipe,
    VaultDatePipe,
    VaultDateTimePipe,
    EconomyOperationsChangesComponent,
    TabViewComponent,
  ],
  templateUrl: './economy-changes-breakdown.component.html',
  styleUrls: ['./economy-changes-breakdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EconomyChangesBreakdownComponent {
  readonly measure = input.required<DashboardEconomyChangesMeasure>();
  readonly measureKey = input<EconomyMeasure>('inicial');
  readonly timeline = input<DashboardEconomyTimelineEntry[]>([]);
  readonly companyNames = input<Record<string, string | null>>({});
  readonly startDate = input<string | null>(null);
  readonly endDate = input<string | null>(null);

  /** Pede à página para abrir os detalhes do empréstimo. */
  readonly loanSelected = output<string>();

  readonly tab = signal<BreakdownTab>('reason');
  readonly expanded = signal<Set<string>>(new Set());

  readonly summary = computed(() => buildEconomyChangeSummary(this.measure(), this.timeline()));
  readonly items = computed(() => changedItems(this.measure()));
  readonly unchanged = computed(() => unchangedCategories(this.measure()));
  readonly operations = computed(() => this.measure().operations ?? []);
  readonly changedOperationCount = computed(
    () => this.operations().filter(op => !isZeroAmount(op.variation)).length
  );

  /** Abas "Por motivo" e "Por operação" (a contagem é a de operações que mudaram). */
  readonly tabItems = computed<TabViewItem[]>(() => [
    { title: 'Por motivo', tabindex: 0, content: '' },
    { title: 'Por operação', tabindex: 1, content: '', count: this.changedOperationCount() },
  ]);

  onTabChange(index: number): void {
    this.tab.set(index === 1 ? 'operation' : 'reason');
  }

  /** Maior valor absoluto entre os itens, para a barra proporcional. */
  readonly maxAbs = computed(() => Math.max(1, ...this.items().map(i => Math.abs(i.value))));

  readonly categoryLabels = ECONOMY_CHANGE_CATEGORY_LABELS;
  readonly categoryHints = ECONOMY_CHANGE_CATEGORY_HINTS;
  readonly triggerLabel = economyTriggerLabel;

  isExpanded(item: DashboardEconomyChangeItem): boolean {
    return this.expanded().has(item.category);
  }

  canExpand(item: DashboardEconomyChangeItem): boolean {
    return item.loans.length > 0 || item.events.length > 0;
  }

  toggle(item: DashboardEconomyChangeItem): void {
    if (!this.canExpand(item)) return;
    const next = new Set(this.expanded());
    if (next.has(item.category)) next.delete(item.category);
    else next.add(item.category);
    this.expanded.set(next);
  }

  barWidth(item: DashboardEconomyChangeItem): number {
    return Math.max(2, Math.round((Math.abs(item.value) / this.maxAbs()) * 100));
  }

  signClass(value: number | null): string {
    if (isZeroAmount(value)) return 'is-zero';
    return (value as number) > 0 ? 'is-up' : 'is-down';
  }

  /** "(-1,6%)" ou "(+0,4%)"; vazio sem percentual. */
  percentLabel(): string {
    const pct = this.summary().percent;
    if (pct === null) return '';
    const text = Math.abs(pct).toLocaleString('pt-BR', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    });
    return `(${pct > 0 ? '+' : '−'}${text}%)`;
  }

  /** ", toda em Mercado e demais, sem nenhum evento no período." */
  sentenceTail(): string {
    const s = this.summary();
    const parts: string[] = [];
    if (s.focus) parts.push(s.focus);
    if (s.eventCount === 0) parts.push('sem nenhum evento no período');
    else parts.push(`com ${s.eventCount} ${s.eventCount === 1 ? 'evento' : 'eventos'}`);
    return parts.join(', ');
  }
}
